import { isFieldErrorCode, type ProviderConfigError } from '@/domain/providerConfig';

import { penKey, toFormKey } from './formKeys';

const PEN_PATH_RE = /^pens\.(\d+)(?:\.(.+))?$/;
const SECRET_PATH_RE = /^secrets\.[A-Za-z]+$/;

/**
 * A server error path as the form names it: `connection.extra_config.scope` is
 * `connection.extra_config__scope`, `pens.101.signal_mappings.feed_amount` is
 * `pens.c101.fields.signal_mappings__feed_amount`, `pens.101.secrets.clientId` is
 * `pens.c101.secrets.clientId`. Secrets and paths outside the form (`from`, `to`) stay.
 */
export function toFormPath(serverPath: string): string {
    if (serverPath.startsWith('connection.'))
        return `connection.${toFormKey(serverPath.slice('connection.'.length))}`;
    const pen = PEN_PATH_RE.exec(serverPath);
    if (pen === null) return serverPath;
    const [, cageId, rest] = pen;
    const prefix = `pens.${penKey(Number(cageId))}`;
    if (rest === undefined) return prefix;
    if (SECRET_PATH_RE.test(rest)) return `${prefix}.${rest}`;
    return `${prefix}.fields.${toFormKey(rest)}`;
}

/** A code this build has no copy for reads as the generic failure, never as a raw key. */
export function errorMessageKey(code: string): string {
    return isFieldErrorCode(code)
        ? `providerConfig.errors.${code}`
        : 'providerConfig.errors.generic';
}

const NESTED_KEY = 'providerConfig.errors.nestedUnderValue';

/**
 * The inline messages as this viewer reads them. "See Other stored settings" points at a
 * section only OptoScale admins have, so a client admin is told whom to ask instead.
 */
export function fieldMessagesFor(
    fields: ServerErrorView['fields'],
    isOptoScaleAdmin: boolean
): ServerErrorView['fields'] {
    if (isOptoScaleAdmin) return fields;
    return fields.map((entry) =>
        entry.messageKey === NESTED_KEY
            ? { ...entry, messageKey: 'providerConfig.errors.nestedUnderValueClient' }
            : entry
    );
}

export interface ServerErrorView {
    /** Inline messages at form paths. */
    fields: { path: string; messageKey: string }[];
    /** The message for the card as a whole, or null when the inline ones say it all. */
    formMessageKey: string | null;
    /** The pens the message is about. */
    cageIds: number[];
}

const GENERIC = 'providerConfig.errors.generic';

function formOnly(formMessageKey: string, cageIds: number[] = []): ServerErrorView {
    return { fields: [], formMessageKey, cageIds };
}

/**
 * What the form shows for a failed save, test or catalog load. Only copy keys come out:
 * a provider's or the request validator's own text can name a token URL or echo input,
 * so it is never shown.
 */
export function serverErrorView(error: ProviderConfigError): ServerErrorView {
    switch (error.kind) {
        case 'invalid':
            return {
                fields: error.errors.map((entry) => ({
                    path: toFormPath(entry.path),
                    messageKey: errorMessageKey(entry.code),
                })),
                // Some paths name values the form does not show, such as advanced ones.
                formMessageKey: 'providerConfig.errors.invalid',
                cageIds: [],
            };
        case 'rowHasData':
            return {
                fields: error.cageIds.map((cageId) => ({
                    path: `pens.${penKey(cageId)}.remove`,
                    messageKey: 'providerConfig.pens.removeHasData',
                })),
                formMessageKey: 'providerConfig.pens.removeHasData',
                cageIds: [...error.cageIds],
            };
        case 'stale':
            return formOnly('providerConfig.toast.conflict');
        case 'alreadyConnected':
            return formOnly('providerConfig.toast.conflict', [...error.cageIds]);
        case 'duplicatePen':
            return formOnly('providerConfig.errors.duplicatePen', [...error.cageIds]);
        case 'testRequired':
            return formOnly('providerConfig.errors.testRequired');
        case 'savedUnread':
            return formOnly('providerConfig.errors.savedUnread');
        case 'encryptionUnavailable':
            return formOnly('providerConfig.errors.encryptionUnavailable');
        case 'rateLimited':
            return formOnly('providerConfig.errors.rateLimited');
        case 'tooLarge':
            return formOnly('providerConfig.errors.tooLarge');
        case 'forbidden':
            return formOnly('providerConfig.errors.forbidden');
        case 'unauthenticated':
            return formOnly('providerConfig.errors.sessionExpired');
        case 'notFound':
            return formOnly('providerConfig.stale.banner');
        case 'badRequest':
        case 'other':
            return formOnly(GENERIC);
    }
}
